import { useCallback, useEffect } from 'react';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { isNilOrEmpty, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { MaxLengths } from 'constants/forms';
import { KCL_DOCUMENT_CHECK_ADMINS } from 'features/common/acl/constants';
import { urls } from 'features/common/fetch/constants';
import {
	prefillLastDocumentStateToStateOrCentralConsent,
	startSaveNewNotice,
} from 'features/views/productionControlDetail/notice/actions';
import { CANNOT_ADD_KOPR_FEEDBACK_PRODUCTION_ALREADY_CLOSED_ERR_MSG } from 'features/views/productionControlDetail/notice/constants';
import { FIELDS } from 'features/views/productionControlDetail/notice/form';
import {
	getFinalStates,
	getProductionControlDetail,
	getProductionControlDetailNotices,
} from 'features/views/productionControlDetail/selectors';

import SingleNotice from './SingleNotice';

export const Notices = () => {
	const dispatch = useDispatch();

	const productionControlDetail = useSelector(getProductionControlDetail);
	const userIsCentral = useSelector((state) => hasPermissionByIds(state)(KCL_DOCUMENT_CHECK_ADMINS));
	const finalStates = useSelector(getFinalStates);
	const notices = useSelector(getProductionControlDetailNotices);
	const hasNotices = !isNilOrEmpty(notices);

	const documentIsNotClosed = productionControlDetail?.solutionStateId !== 3;
	const documentInNotFoundState = productionControlDetail?.documentState === 1;

	const formData = useFormData();
	const formIsInitiated = isObject(formData);
	const isStateSelected = !isNilOrEmpty(formData?.documentState) || !isNilOrEmpty(formData?.centralConsent);

	const showSaveButton = (!userIsCentral && documentIsNotClosed) || userIsCentral;
	const isTextOrStateFilled = !isNilOrEmpty(formData?.noticeTextContent) || isStateSelected;

	const showState = !userIsCentral && documentInNotFoundState && documentIsNotClosed;
	const showCentralConsent = userIsCentral && documentInNotFoundState;

	const showAddNotice = (!userIsCentral && productionControlDetail?.solutionStateId !== 3) || userIsCentral;
	const showNoticeSection = showAddNotice || hasNotices;

	const showDivider = showAddNotice && hasNotices;

	useEffect(() => {
		if (formIsInitiated) {
			dispatch(
				prefillLastDocumentStateToStateOrCentralConsent({
					showState,
					showCentralConsent,
					notices,
				}),
			);
		}
	}, [dispatch, formIsInitiated, showState, showCentralConsent, notices]);

	const disabledForSaveButton = ({ userIsCentral, isTextOrStateFilled, documentIsNotClosed }) => {
		if (userIsCentral) {
			return !isTextOrStateFilled;
		} else {
			return !(documentIsNotClosed && isTextOrStateFilled);
		}
	};

	const handleSaveNotice = useCallback(async () => {
		dispatch(startSaveNewNotice());
	}, [dispatch]);

	const ignoreErrorsForKoprNotices = useIgnoreErrorsWhen({
		fetchStorePath: urls.koprNewNotice,
		attributePath: ['metaData', 'parsedContent', 'message'],
		value: CANNOT_ADD_KOPR_FEEDBACK_PRODUCTION_ALREADY_CLOSED_ERR_MSG,
	});

	return (
		<Grid size={12}>
			<ComponentLoader fetchStorePath={urls.koprNewNotice} ignoreErrors={ignoreErrorsForKoprNotices}>
				{showNoticeSection && (
					<Paper sx={{ p: 3, borderRadius: 2 }}>
						<Stack spacing={5}>
							{showAddNotice && (
								<Stack spacing={2}>
									<SectionHeader
										text={getLabelMessage(
											`productionControlDetail:${!userIsCentral ? 'notice' : 'noticeByCentral'}`,
										)}
									/>
									<Grid container>
										<Grid size={12}>
											<Grid container spacing={2}>
												<Grid size={12}>
													<TextField
														size="small"
														fieldName={FIELDS.noticeTextContent}
														label={getLabelMessage(
															`productionControlDetail:${
																!userIsCentral ? 'notice' : 'noticeByCentral'
															}`,
														)}
														fullWidth
														multiline
														rows={4}
														slotProps={{
															htmlInput: {
																maxLength: MaxLengths.NOTE,
															},
														}}
													/>
												</Grid>
												<Grid size={12}>
													<Grid
														container
														spacing={2}
														sx={{
															justifyContent: 'space-between',
														}}
													>
														<Grid size={3}>
															{showState && (
																<Autocomplete
																	size="small"
																	fieldName={FIELDS.documentState}
																	label={getLabelMessage(
																		'productionControlDetail:documentState',
																	)}
																	options={finalStates}
																	labelPropName="description"
																	disabled={!documentIsNotClosed}
																/>
															)}
															{showCentralConsent && (
																<Autocomplete
																	size="small"
																	fieldName={FIELDS.centralConsent}
																	label={getLabelMessage(
																		'productionControlDetail:centralConsent',
																	)}
																	options={finalStates}
																	labelPropName="description"
																/>
															)}
														</Grid>
														<Grid size={3}>
															{showSaveButton && (
																<SubmitButton
																	variant="contained"
																	fullWidth
																	disabled={disabledForSaveButton({
																		userIsCentral,
																		isTextOrStateFilled,
																		documentIsNotClosed,
																	})}
																	onClick={handleSaveNotice}
																	text={getLabelMessage('common:save')}
																/>
															)}
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Stack>
							)}
							{showDivider && <Divider />}
							<Stack spacing={5}>
								{hasNotices &&
									notices.map((item) => (
										<SingleNotice
											key={item?.id}
											id={item?.id}
											item={item}
											documentInNotFoundState={documentInNotFoundState}
										/>
									))}
							</Stack>
						</Stack>
					</Paper>
				)}
			</ComponentLoader>
		</Grid>
	);
};

export default Notices;
